<script setup>
import useStore from "./../store"
import FieldError from "./FieldError.vue"
import {storeToRefs} from "pinia";
import {computed, ref, watch} from "vue";
import moment from "moment";
import ExperienceIcon from "../../components/ExperienceIcon.vue";
import VLazyImage from "../../plugins/v-lazy-image/dist/v-lazy-image";
import {useField} from "vee-validate";
import ErrorWrapper from "./ErrorWrapper.vue";

const props = defineProps({
  errors: Array,
  name: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false
  },
  submitted: {
    type: Boolean,
    default: false
  },
  attraction: {
    type: [Object, Array],
    default: () => {},
  },
  value: {
    type: [String, Boolean, Number],
    default: '',
  }
})

const model = defineModel()
const time = defineModel('time')
const localValue = ref(false);

const isWalkInOnlyToday = computed(() => {
  const ranges = props.attraction?.walkin_only_dates ?? []
  if (!ranges.length) return false
  const today = moment()
  return ranges.some(range =>
      today.isBetween(moment(range.start), moment(range.end), 'day', '[]')
  )
})

const localDetails = computed(() => {
  const experiences = window.bookingWidget?.experiences ?? [];

  if (!props.attraction?.code) {
    return null;
  }

  return experiences.find(e => e.code === props.attraction.code)
});

const pageStyle = computed(() => {
  return localDetails.value?.page_style ?? 'default'
})

watch(() => model.value, async (newModel, oldModel) => {
  time.value = false
})

watch(() => localValue.value, async (newModel, oldModel) => {
  console.log('localValue', localValue.value)
  console.log('model', model.value)
})


const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  meta,
} = useField(props.name, undefined, {
  initialValue: props.value,
});


const setOption = (option) => {
  handleChange(option)
}

</script>
<template>
  <div v-if="localDetails">
    <div class="pt-4 relative">

      <div
           class="relative h-full rounded-3xl bg-light overflow-hidden ">

        <a class="relative block w-full">
          <v-lazy-image
              v-if="localDetails.image"
              :class="'w-full select-none object-cover'"
              use-picture
              width="459"
              height="224"
              draggable="false"
              :alt="`${localDetails.title} image`"
              :src="`${localDetails.image}?tr=w-800,h-500`"
              :src-placeholder="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-800,h-500`"
          >
            <source
                :srcset="
              `
              ${localDetails.image}?tr=w-800,h-500,
              ${localDetails.image}?tr=w-1200,h-700 2x
            `
            "
            />
          </v-lazy-image>

          <img v-else class="w-full select-none object-cover"
               :src="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-380,h-220`"
               alt="default placeholder"
               loading="lazy">

          <div class="absolute -bottom-0.5 left-0 z-10 w-full">
            <div class="-mr-1">
              <svg class="w-full fill-light transition-colors" viewBox="0 0 384 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 1.51621V30.5001H384V3.35662C369.413 9.49075 342.427 20.3788 306.872 24.9794C273.676 29.2747 257.094 27.7398 236.308 27.2797C215.529 26.8198 174.496 20.3788 161.368 17.1584C148.239 13.938 91.8974 3.81651 68.9231 1.51621C50.5436 -0.324025 15.3162 0.749447 0 1.51621Z"></path>
              </svg>
            </div>
          </div>

          <div
              class="absolute bottom-0 left-6 z-30 flex justify-center items-center w-14 h-14 rounded-full"
              :class="`bg-${pageStyle}-primary`"
          >
            <experience-icon :page-style="pageStyle"></experience-icon>
          </div>
        </a>

        <div class="relative px-6 pt-4 pb-6 transition-colors">
          <error-wrapper :submitted="submitted" :errors="errors">

            <h3 class="mb-1 text-xl lg:text-2xl"
                :class="`text-${pageStyle}-secondary`">
              <span v-html="localDetails.title"></span>
            </h3>

            <span v-if="localDetails.short_address"
               class="flex items-center gap-3 text-grey text-sm lg:text-base font-regular">

              <svg :class="`fill-${pageStyle}-secondary`"
                   width="14"
                   height="18"
                   viewBox="0 0 14 18"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M7.80859 17.5914C7.38672 18.1188 6.57812 18.1188 6.15625 17.5914C4.36328 15.3063 0.25 9.85706 0.25 6.76331C0.25 3.03674 3.27344 0.0133057 7 0.0133057C10.7266 0.0133057 13.75 3.03674 13.75 6.76331C13.75 9.85706 9.63672 15.3063 7.80859 17.5914ZM7 4.51331C6.19141 4.51331 5.45312 4.97034 5.03125 5.63831C4.64453 6.34143 4.64453 7.22034 5.03125 7.88831C5.45312 8.59143 6.19141 9.01331 7 9.01331C7.77344 9.01331 8.51172 8.59143 8.93359 7.88831C9.32031 7.22034 9.32031 6.34143 8.93359 5.63831C8.51172 4.97034 7.77344 4.51331 7 4.51331Z"></path>
              </svg>

              {{ localDetails.short_address }}
            </span>

            <div v-if="isWalkInOnlyToday"
                 v-text="localDetails.walkin_only_message"
                 class="rounded-2xl border border-default bg-white px-5 py-4 text-left text-sm text-dark shadow-xl mt-4 lg:block lg:pointer-events-none lg:transition-opacity lg:duration-200 lg:group-hover:opacity-100">
            </div>

            <div v-if="attraction.rezdy_product_options && attraction.rezdy_product_options.length > 0" data-testid="booking-product-options">
              <p class="mb-4 my-8">Which experience would you like to book? <br> (You can add another later). </p>
              <ul class="divide-y-2 divide-dashed divide-primary space-y-4">
                <li v-for="option in attraction.rezdy_product_options" class="pt-4">
                  <label class="flex items-center justify-between cursor-pointer" data-testid="booking-product-option">
                    <span class="flex flex-col gap-2">
                      <span v-html="option.title" class="font-semibold"></span>
                      <span v-html="option.desc"></span>
                    </span>

                    <span class="flex justify-end items-center">
                      <span class="w-6 h-6 rounded-full border border-grey bg-white p-[2px]">
                        <span v-if="option.product === inputValue"
                              class="bg-grey flex rounded-full h-full w-full border border-grey"></span>
                        <input type="checkbox"
                               :name="name"
                               class="hidden"
                               @change="setOption(option.product)"
                               :value="option.product">
                      </span>
                    </span>
                  </label>
                </li>
              </ul>
            </div>
            <FieldError :errors="props.errors" />
          </error-wrapper>
          </div>
      </div>
    </div>
  </div>
</template>
